import React, { useState, useRef, useEffect } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";

const JF = "'Josefin Sans', sans-serif";
const PJ = "'Plus Jakarta Sans', sans-serif";

const EMAIL = "harry.hphu@gmail.com";

/* ─── tiny helpers ─────────────────────────────────────────── */
function useAnimateIn() {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.opacity = "0";
    node.style.transform = "translateY(18px)";
    const t = setTimeout(() => {
      node.style.transition = "opacity 0.55s ease, transform 0.55s ease";
      node.style.opacity = "1";
      node.style.transform = "translateY(0)";
    }, 60);
    return () => clearTimeout(t);
  }, []);
  return ref;
}

/* ─── Field ─────────────────────────────────────────────────── */
function Field({ label, type = "text", value, onChange, multiline, placeholder, required }) {
  const [focused, setFocused] = useState(false);

  const labelColor     = useColorModeValue("#aaaaaa", "#555555");
  const labelActive    = useColorModeValue("#333333", "#cccccc");
  const lineIdle       = useColorModeValue("#dcdcdc", "#2e2e2e");
  const lineActive     = useColorModeValue("#222222", "#dddddd");
  const inputColor     = useColorModeValue("#111111", "#eeeeee");
  const placeholderCol = useColorModeValue("#cccccc", "#404040");
  const requiredColor  = useColorModeValue("#c0392b", "#e06c75");

  const sharedStyle = {
    width: "100%",
    background: "transparent",
    border: "none",
    outline: "none",
    fontFamily: PJ,
    fontSize: "16px",
    color: inputColor,
    padding: "8px 0",
    resize: "none",
    lineHeight: 1.7,
  };

  return (
    <Box mb={8}>
      <Text
        fontFamily={JF}
        fontSize="10px"
        fontWeight="700"
        letterSpacing="0.22em"
        textTransform="uppercase"
        color={focused || value ? labelActive : labelColor}
        mb="6px"
        sx={{ transition: "color 0.2s ease" }}
      >
        {label}{required && <Box as="span" ml="3px" color={requiredColor}>*</Box>}
      </Text>

      <Box
        borderBottom="1px solid"
        borderColor={focused ? lineActive : lineIdle}
        sx={{ transition: "border-color 0.25s ease" }}
        pb="2px"
      >
        {multiline ? (
          <textarea
            rows={5}
            value={value}
            onChange={e => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            style={{
              ...sharedStyle,
              display: "block",
              "--placeholder-color": placeholderCol,
            }}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            style={sharedStyle}
          />
        )}
      </Box>
    </Box>
  );
}

/* ─── CopyEmailButton ────────────────────────────────────────── */
function CopyEmailButton() {
  const [copied, setCopied] = useState(false);
  const metaColor  = useColorModeValue("#aaaaaa", "#555555");
  const hoverColor = useColorModeValue("#333333", "#cccccc");
  const okColor    = useColorModeValue("#27ae60", "#69db7c");

  function handleCopy() {
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="8px"
      cursor="pointer"
      onClick={handleCopy}
      role="button"
      aria-label="Copy email address"
      sx={{
        "& span": { transition: "color 0.2s ease" },
        "&:hover span": { color: hoverColor },
      }}
    >
      <Box
        as="span"
        fontFamily={JF}
        fontSize="11px"
        letterSpacing="0.16em"
        textTransform="uppercase"
        color={copied ? okColor : metaColor}
        sx={{ transition: "color 0.25s ease" }}
      >
        {copied ? "Copied!" : EMAIL}
      </Box>
      <Box
        as="span"
        color={copied ? okColor : metaColor}
        fontSize="13px"
        sx={{ transition: "color 0.25s ease" }}
      >
        <i className={copied ? "ri-check-line" : "ri-clipboard-line"} />
      </Box>
    </Box>
  );
}

/* ─── Contact page ───────────────────────────────────────────── */
export default function Contact() {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [message, setMessage] = useState("");
  const [sent,    setSent]    = useState(false);

  const wrapRef     = useAnimateIn();
  const isDark      = useColorModeValue(false, true);

  const headingColor = useColorModeValue("#0c0c0c", "#f0f0f0");
  const subColor     = useColorModeValue("#888888", "#666666");
  const divColor     = useColorModeValue("#e8e8e8", "#2a2a2a");
  const btnBg        = useColorModeValue("#111111", "#eeeeee");
  const btnText      = useColorModeValue("#ffffff", "#111111");
  const btnHoverBg   = useColorModeValue("#333333", "#cccccc");
  const watermarkCol = useColorModeValue("rgba(0,0,0,0.04)", "rgba(255,255,255,0.03)");
  const orColor      = useColorModeValue("#cccccc", "#333333");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  function handleSend(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    const subject = encodeURIComponent(`Hello from ${name.trim()}`);
    const body    = encodeURIComponent(
      `Hi Harry,\n\n${message.trim()}\n\n—\n${name.trim()}\n${email.trim()}`
    );
    window.open(
      `https://mail.google.com/mail/?view=cm&to=${EMAIL}&su=${subject}&body=${body}`,
      "_blank"
    );
    setSent(true);
    setTimeout(() => setSent(false), 3500);
  }

  const canSend = name.trim() && email.trim() && message.trim();

  return (
    <Box as="section" pt={14} pb={20} ref={wrapRef} position="relative" overflow="hidden">
      <Helmet>
        <title>Contact | Huy Phu</title>
        <meta name="description" content="Get in touch with Huy Phu — Senior QA Engineer and Security Engineer based in Los Angeles, CA." />
        <meta property="og:title" content="Contact | Huy Phu" />
        <meta property="og:url" content="https://hqphu.com/contact" />
      </Helmet>

      {/* Watermark glyph */}
      <Box
        position="absolute"
        top="-20px"
        right="-30px"
        fontSize="260px"
        lineHeight={1}
        color={watermarkCol}
        fontFamily={JF}
        fontWeight="700"
        userSelect="none"
        pointerEvents="none"
        aria-hidden
        zIndex={0}
      >
        ✉
      </Box>

      <Box position="relative" zIndex={1}>

        {/* Header */}
        <Box mb={10}>
          <Text
            fontFamily={JF}
            fontSize="10px"
            fontWeight="700"
            letterSpacing="0.3em"
            textTransform="uppercase"
            color={subColor}
            mb={3}
          >
            Get in touch
          </Text>
          <Text
            fontFamily={PJ}
            fontSize="34px"
            fontWeight="700"
            color={headingColor}
            lineHeight={1.2}
            mb={3}
          >
            Say hello.
          </Text>
          <Text fontFamily={PJ} fontSize="15px" color={subColor} lineHeight={1.7}>
            Have a question, a project idea, or just want to connect?
            I read every message.
          </Text>
        </Box>

        <Box h="1px" bg={divColor} mb={10} />

        {/* Form */}
        <Box as="form" onSubmit={handleSend} noValidate>
          <Field
            label="Your name"
            value={name}
            onChange={setName}
            placeholder="Jane Smith"
            required
          />
          <Field
            label="Your email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="jane@example.com"
            required
          />
          <Field
            label="Message"
            value={message}
            onChange={setMessage}
            multiline
            placeholder="What's on your mind?"
            required
          />

          {/* Send button */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={4} mt={2}>
            <button
              type="submit"
              disabled={!canSend}
              style={{
                fontFamily: JF,
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                background: sent ? (isDark ? "#1a3a1a" : "#eafbf0") : (canSend ? btnBg : (isDark ? "#1e1e1e" : "#e8e8e8")),
                color: sent ? (isDark ? "#69db7c" : "#27ae60") : (canSend ? btnText : (isDark ? "#444" : "#bbb")),
                border: "none",
                padding: "13px 28px",
                cursor: canSend ? "pointer" : "not-allowed",
                borderRadius: "3px",
                transition: "background 0.25s ease, color 0.25s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={e => { if (canSend && !sent) e.currentTarget.style.background = btnHoverBg; }}
              onMouseLeave={e => { if (canSend && !sent) e.currentTarget.style.background = btnBg; }}
            >
              <i className={sent ? "ri-check-line" : "ri-send-plane-line"} style={{ fontSize: "13px" }} />
              {sent ? "Opening mail client…" : "Send message"}
            </button>

            {/* Divider + copy option */}
            <Box display="flex" alignItems="center" gap="14px">
              <Box h="14px" w="1px" bg={orColor} />
              <CopyEmailButton />
            </Box>
          </Box>
        </Box>

        {/* Footer note */}
        <Box mt={14} h="1px" bg={divColor} />
        <Text fontFamily={JF} fontSize="10px" letterSpacing="0.18em" color={subColor} mt={5} textTransform="uppercase">
          Typically responds within 48 hours
        </Text>
      </Box>

      {/* Placeholder styles for inputs */}
      <style>{`
        input::placeholder, textarea::placeholder {
          color: ${isDark ? "#3a3a3a" : "#cccccc"};
          font-family: ${PJ};
        }
        textarea { overflow-y: auto; }
      `}</style>
    </Box>
  );
}
